import { defineStore } from 'pinia';
import { ref, computed } from 'vue';
import { login as loginApi, refreshToken as refreshTokenApi, getMyPermissions } from '@/api/auth';
import { getProfile, updateProfile } from '@/api/user';
import router from '@/router';

export const useUserStore = defineStore('user', () => {
  const accessToken = ref(localStorage.getItem('accessToken') || '');
  const refreshToken = ref(localStorage.getItem('refreshToken') || '');
  const userInfo = ref<Record<string, unknown>>({});
  const roleCodes = ref<string[]>([]);
  const permissionCodes = ref<string[]>([]);
  const tenantId = ref('');

  const isLoggedIn = computed(() => !!accessToken.value);
  const isAdmin = computed(() =>
    roleCodes.value.includes('super_admin') || roleCodes.value.includes('tenant_admin')
  );

  function hasPermission(code: string): boolean {
    return permissionCodes.value.includes(code);
  }

  /**
   * C-7：统一的权限判断入口（菜单/路由守卫/页面按钮共用）。
   * 管理员角色直通（与后端 requireRoles(TENANT_ADMIN) 口径一致），
   * 其余角色按权限码判定。
   */
  function can(code: string): boolean {
    return isAdmin.value || permissionCodes.value.includes(code);
  }

  const profileLoaded = ref(false);
  const permissionsLoaded = ref(false);

  async function fetchPermissions() {
    try {
      const res = await getMyPermissions();
      if (res?.success) permissionCodes.value = res.data || [];
    } catch {}
    permissionsLoaded.value = true;
  }

  /** 路由守卫用：登录态下确保 userInfo 与权限码已加载（刷新页面后只取一次） */
  async function ensureProfileLoaded(): Promise<void> {
    if (!accessToken.value) return;
    const jobs: Promise<unknown>[] = [];
    if (!profileLoaded.value) jobs.push(fetchProfile());
    if (!permissionsLoaded.value) jobs.push(fetchPermissions());
    await Promise.allSettled(jobs);
  }

  /** Decode JWT payload without verification (client-side) */
  function decodeToken(token: string): Record<string, unknown> | null {
    try {
      const parts = token.split('.');
      if (parts.length !== 3) return null;
      return JSON.parse(atob(parts[1]));
    } catch {
      return null;
    }
  }

  /** Check if the current access token is expired */
  function isTokenExpired(): boolean {
    if (!accessToken.value) return true;
    const payload = decodeToken(accessToken.value);
    if (!payload?.exp) return false; // can't determine, assume not expired
    return Date.now() >= (payload.exp as number) * 1000;
  }

  /** Try to refresh the access token using the stored refresh token */
  async function refreshAccessToken(): Promise<boolean> {
    if (!refreshToken.value) return false;
    try {
      const res = await refreshTokenApi(refreshToken.value);
      if (res?.success && res.data?.accessToken) {
        accessToken.value = res.data.accessToken;
        localStorage.setItem('accessToken', res.data.accessToken);
        return true;
      }
    } catch {
      // refresh failed
    }
    return false;
  }

  async function login(account: string, password: string) {
    const res = await loginApi({ account, password });
    if (res?.success) {
      accessToken.value = res.data.accessToken;
      refreshToken.value = res.data.refreshToken || '';
      userInfo.value = res.data.user;
      roleCodes.value = (res.data.user?.roleCodes as string[]) || [];
      tenantId.value = res.data.user?.tenantId || '';
      profileLoaded.value = true;
      localStorage.setItem('accessToken', res.data.accessToken);
      if (res.data.refreshToken) {
        localStorage.setItem('refreshToken', res.data.refreshToken);
      }
      fetchPermissions();
    }
    return res;
  }

  async function fetchProfile() {
    const res = await getProfile();
    if (res?.success) {
      userInfo.value = res.data;
      roleCodes.value = res.data?.roleCodes || [];
      tenantId.value = res.data?.tenantId || '';
    }
    profileLoaded.value = true;
    return res;
  }

  async function updateUserInfo(data: { username?: string; email?: string; avatar?: string; phone?: string | null; address?: string | null }) {
    const res = await updateProfile(data);
    if (res?.success) {
      userInfo.value = { ...userInfo.value, ...res.data };
    }
    return res;
  }

  /**
   * 服务端注销：将当前 access/refresh token 加入黑名单（S-9）。
   * 尽力而为：用原生 fetch 绕开 axios 拦截器（避免 401 时递归触发 logout），失败不阻塞本地登出。
   */
  function revokeServerTokens() {
    const access = accessToken.value;
    const refresh = refreshToken.value;
    if (!access && !refresh) return;
    try {
      void fetch('/api/auth/logout', {
        method: 'POST',
        keepalive: true,
        headers: {
          'Content-Type': 'application/json',
          ...(access ? { Authorization: `Bearer ${access}` } : {}),
        },
        body: JSON.stringify({ refreshToken: refresh || undefined }),
      }).catch(() => {});
    } catch {}
  }

  function logout(redirectQuery?: Record<string, string>, loginPath?: string) {
    revokeServerTokens();
    accessToken.value = '';
    refreshToken.value = '';
    userInfo.value = {};
    roleCodes.value = [];
    permissionCodes.value = [];
    tenantId.value = '';
    profileLoaded.value = false;
    permissionsLoaded.value = false;
    localStorage.removeItem('accessToken');
    localStorage.removeItem('refreshToken');
    const query = redirectQuery ? { ...redirectQuery } : undefined;
    router.push({ path: loginPath || '/login', query });
  }

  /** S-23：自助改密成功后同步清除本地强制改密标记 */
  function clearMustChangePassword() {
    userInfo.value = { ...userInfo.value, mustChangePassword: false };
  }

  return {
    accessToken,
    refreshToken,
    userInfo,
    roleCodes,
    permissionCodes,
    tenantId,
    isLoggedIn,
    isAdmin,
    hasPermission,
    can,
    profileLoaded,
    permissionsLoaded,
    ensureProfileLoaded,
    fetchPermissions,
    isTokenExpired,
    refreshAccessToken,
    decodeToken,
    login,
    fetchProfile,
    updateUserInfo,
    logout,
    clearMustChangePassword,
  };
});
